import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';

export interface KlingOmniVideoParams {
  /**
   * 视频生成提示词,最多2500字符
   * @length 0 - 2500
   */
  prompt: string;
  /**
   * 参考图片列表,支持URL或base64格式,URL会自动转换为base64
   */
  image_list?: Array<{
    /**
     * 图片URL或base64数据
     */
    image_url?: string;
    /**
     * 图片URL或base64数据(兼容字段)
     */
    image?: string;
    /**
     * 图片类型,如first_frame(首帧)或end_frame(尾帧)
     * @values first_frame | end_frame
     */
    type?: 'first_frame' | 'end_frame';
  }>;
  /**
   * 参考视频列表,通过URL方式获取 ● 可作为特征参考视频,也可作为待编辑视频,默认为待编辑视频 ● 通过refer_type参数区分参考视频类型: feature为特征参考视频,base为待编辑视频 ● 参考视频为待编辑视频时,不能定义视频首尾帧 ● 通过keep_original_sound参数选择是否保留视频原声,yes为保留,no为不保留(对feature和base类型均生效) ● 视频格式仅支持MP4/MOV ● 仅支持时长≥3秒且≤10秒的视频 ● 视频宽高尺寸需介于720px(含)和2160px(含)之间 ● 视频帧率基于24fps～60fps,生成视频时会输出为24fps ● 至多仅支持上传1段视频,视频大小不超过200MB
   */
  video_list?: Array<{
    /**
     * 视频URL地址,支持MP4/MOV格式
     */
    video_url: string;
    /**
     * 参考视频类型: feature(特征参考视频)或base(待编辑视频),默认为base
     * @default "base"
     * @values feature | base
     */
    refer_type?: 'feature' | 'base';
    /**
     * 是否保留视频原声: yes(保留)或no(不保留)
     * @values yes | no
     */
    keep_original_sound?: 'yes' | 'no';
  }>;
  /**
   * 自定义元素列表
   */
  element_list?: Array<{
  }>;
  /**
   * 生成模式,Omni仅支持pro模式
   * @values pro
   */
  mode?: 'pro';
  /**
   * 视频宽高比，除参考外，必填
   * @values 16:9 | 9:16 | 1:1
   */
  aspect_ratio?: '16:9' | '9:16' | '1:1';
  /**
   * 视频时长(秒),Omni支持3-10秒
   * @range 3 - 10
   */
  duration?: number;
  /**
   * 外部任务ID
   */
  external_task_id?: string;
}

export interface KlingOmniVideoResource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * kling_omni_video
 *
 * <p>Kling AI Omni Video 多模态视频生成接口，支持文本、图片、视频和自定义元素等多模态输入</p>
 * <p>API Documentation:&nbsp;<a href="https://docs.qingque.cn/d/home/eZQCRUy_LWt70n5Wz2sTiLV6J?identityId=2LKTadU2MSa#section=h.hhdxwtuxzvyf">https://docs.qingque.cn/d/home/eZQCRUy_LWt70n5Wz2sTiLV6J?identityId=2LKTadU2MSa#section=h.hhdxwtuxzvyf</a></p>
 * 
 *
 * @example
 * ```typescript
 * import { initSeacloud, klingOmniVideo } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await klingOmniVideo({
 *   // 参数见 KlingOmniVideoParams 接口定义
 * });
 *
 * console.log(result); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 KlingOmniVideoParams 接口定义
 * @returns 生成的资源列表
 */
export async function klingOmniVideo(
  params: KlingOmniVideoParams,
): Promise<KlingOmniVideoResource[]> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 5000,
    maxAttempts: 120,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'kling_omni_video',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: KlingOmniVideoResource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
